
import { useSidClient } from "@/plugins/auth";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "@/store";
import { defineComponent, onMounted, onUnmounted, ref } from "vue";
import { SID_ERROR_MSG_NOT_AUTHENTICATED, SID_ERROR_MSG_UNAUTHORIZED } from "@sid/sid_client";
import { Form, Field } from "vee-validate";
import { checkConfirmationCodeSms } from "@/commons/inputCheckCommon";
import * as Yup from "yup";
import useDeterring from "@/commons/useDeterring";
import { AuthPhoneItem } from "@/modules/authPhone";
import { maskPhoneNumber } from "@/commons/commonUtility";

export default defineComponent({
    name: "AuthPhoneConfirm",
    components: {
        Form,
        Field
    },
    setup() {
        const sidClient = useSidClient();
        const router = useRouter();
        const route = useRoute();
        const store = useStore();
        const { isDeterringRef, asyncDeterringWrap } = useDeterring();

        const authPhoneItem = AuthPhoneItem.inject();
        // const userAuthPhone = authPhoneItem.authPhone as UserBaseAuthPhone;

        const nowAuthPhoneRef = ref("");
        const newAuthPhoneRef = ref("");
        const phoneNumber = ref<string | null>(null);
        const confirmationCodeRef = ref("");
        const errorMsgRef = ref("");
        const processMode = ref("");
        const schema = Yup.object().shape({
            confirmationCode: checkConfirmationCodeSms
        });
        const COOLDOWN_SECONDS = 60;
        const cooldown = ref(0);
        let cooldownIntervalId: number | null = null;

        /**
         * ボタン押下時処理
         * @async
         */
        const confirmationCodeProcess = async () => {
            // 認証番号の確認
            await sidClient.verifyAuthPhone(confirmationCodeRef.value)
                .then(() => {                    
                    // 認証用電話番号登録完了画面へ遷移
                    router.push({ name: "AuthPhoneComplete", query: { processMode: processMode.value } });
                })
                .catch(reason => {
                    if (reason.message === SID_ERROR_MSG_NOT_AUTHENTICATED || reason.message === SID_ERROR_MSG_UNAUTHORIZED) {
                        // ログイン画面へ遷移
                        router.push({ name: "Login" });
                    } else if (reason.response?.data === "invalid verification code") {
                        errorMsgRef.value = "コードが確認できませんでした。正しいコードを入力して再実施ください。";
                    } else if (reason.response?.data === "This phone number cannot be registered") {
                        errorMsgRef.value = "入力された認証用電話番号はご利用になれません。他の認証用電話番号を入力してください。";
                    } else {
                        errorMsgRef.value = "認証用電話番号登録に失敗しました。後ほど再度お試し下さい。";
                    }
                })
                .finally(async () => {
                    // 認証用電話番号情報再取得して保持
                    await store.dispatch("fetchUserAuthPhone");
                    authPhoneItem.setAuthPhone(store.state.userAuthPhone);
                    return;
                });

        };

        /**
         * 確認コード再送ボタン押下時処理
         * @async
         */
        const resendAttrConfirmationCode = async () => {

            // 認証用電話番号登録再実施
            await sidClient.setAuthPhone(phoneNumber.value as string)
                .catch(reason => {
                    if (reason.message === SID_ERROR_MSG_NOT_AUTHENTICATED || reason.message === SID_ERROR_MSG_UNAUTHORIZED) {
                        // ログイン画面へ遷移
                        router.push({ name: "Login" });
                    } else if (reason.response?.data === "the authPhone has already been changed") {
                        errorMsgRef.value = "既に認証用電話番号登録は完了しています。";
                    } else if (reason.response?.data === "the authPhone address is not allowed") {
                        errorMsgRef.value = "この認証用電話番号の登録はできません。";
                    } else {
                        errorMsgRef.value = "認証用電話番号登録に失敗しました。後ほど再度お試し下さい。";
                    }
                });
        };

        /**
         * 連続再送抑止用のタイマー設定
         */
        const startCooldown = () => {
            if (cooldown.value > 0) return;
            cooldown.value = COOLDOWN_SECONDS;
            if (cooldownIntervalId) clearInterval(cooldownIntervalId);
            cooldownIntervalId = window.setInterval(() => {
                cooldown.value -= 1;
                if (cooldown.value <= 0 && cooldownIntervalId) {
                    clearInterval(cooldownIntervalId);
                    cooldownIntervalId = null;
                }
            }, 1000);
        };

        /**
         * 再送ボタンクリック
         * @param ctx
         */
        const onResendClick = (ctx: any) => {
            if (cooldown.value > 0 || isDeterringRef.value) return;
            startCooldown();
            asyncDeterringWrap(resendAttrConfirmationCode, ctx);
        };

        /**
         * キャンセルボタン押下時処理
         * @async
         */
        const cancelAuthPhone = async() => {
            router.push({ name: "AuthPhone" });
        };

        /**
         * ページを離れる場合の確認アラート起動
         * @event
         */
        const confirmSave = (event: any) => {
            event.returnValue = "保存されませんが、よろしいですか？";
        };

        onMounted(() => {
            nowAuthPhoneRef.value = route.query.nowAuthPhone as string;
            newAuthPhoneRef.value = route.query.newAuthPhone as string;
            phoneNumber.value = route.query.phoneNumber as string;
            processMode.value = route.query.processMode as string;
            window.addEventListener("beforeunload", confirmSave);
        });

        onUnmounted(() => {
            window.removeEventListener("beforeunload", confirmSave);
            if (cooldownIntervalId) clearInterval(cooldownIntervalId);
        });

        return {
            isDeterringRef,
            asyncDeterringWrap,
            newAuthPhoneRef,
            nowAuthPhoneRef,
            phoneNumber,
            confirmationCodeRef,
            errorMsgRef,
            schema,
            confirmationCodeProcess,
            resendAttrConfirmationCode,
            cooldown,
            onResendClick,
            processMode,
            cancelAuthPhone,
            maskPhoneNumber
        };
    },
});
